import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import moment from 'moment';
import produce from 'immer';
import { cloneDeep, isEmpty } from 'lodash';
import InputMask from 'react-input-mask';
import { capitalCase } from 'change-case';
import { MobileDatePicker } from '@mui/lab';
import { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Add, Check, Edit, Info } from '@mui/icons-material';
import { getExtensionArrayByURLs, getFhirIdFromEntity, phoneFormat } from 'src/utils/fhir';
import { useOrganizations, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { convertValueToValueSet } from 'src/careflow/common/common-utils';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import { OrganizationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { ContactPointUse, PatientGender } from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { capitalize } from 'src/utils/string';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import AddAddressLoading from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddAddressLoading';

interface ConfirmMemberInfoProps {
  patient: WrappedPatient | null;
  isOpen: boolean;
  handlerIsOpen: Function;
  handlePatient: (data: any) => Promise<any>;
  handleConfirmMember: (data: any) => Promise<any>;
  refreshPatient: () => Promise<any>;
}

export default function ConfirmMemberInfo({
  patient,
  isOpen,
  handlerIsOpen,
  handlePatient,
  handleConfirmMember,
  refreshPatient,
}: ConfirmMemberInfoProps) {
  const { i18n } = useLocales();
  const { componentsData } = useTenantConfigData();
  const { workflow, patient: patientConfig } = componentsData;

  const [firstNameEditMode, setFirstNameEditMode] = useState(false);
  const [middleNameEditMode, setMiddleNameEditMode] = useState(false);
  const [secondNameEditMode, setSecondNameEditMode] = useState(false);
  const [dobEditMode, setDobEditMode] = useState(false);
  const [genderEditMode, setGenderEditMode] = useState(false);
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [phoneEditMode, setPhoneEditMode] = useState(false);
  const [languageEditMode, setLanguageEditMode] = useState(false);
  const [addressEditMode, setAddressEditMode] = useState(false);
  const [organizationEditMode, setOrganizationEditMode] = useState(false);
  const [patientInfoLoaded, setPatientInfoLoaded] = useState(false);
  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [externalError, setExternalError] = useState(false);
  const [address, setAddress] = useState<any>(null);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const [organizations, { isFetching: isOrganizationLoading }] = useOrganizations({
    map: OrganizationWrapper,
  });
  const [simpleLanguage] = useValueSet('simple-language', { map: ValueSetWrapper });
  const [housingStatus] = useValueSet('udsplus-housing-status-codes', { map: ValueSetWrapper });

  const isAllowedAddressHousingStatus = checkAclValidation({
    acls: [crsAcls.CRS.PATIENT.ADDRESS_DETAILS.ADD_HOUSING_STATUS],
  });

  useEffect(() => {
    setPatientInfoLoaded(false);
    if (!isOpen) {
      setFirstNameEditMode(false);
      setMiddleNameEditMode(false);
      setSecondNameEditMode(false);
      setDobEditMode(false);
      setGenderEditMode(false);
      setEmailEditMode(false);
      setPhoneEditMode(false);
      setLanguageEditMode(false);
      setAddressEditMode(false);
      setOrganizationEditMode(false);
      setExternalError(false);
    }
    setPatientInfoLoaded(true);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen]);

  const saveChanges = async (data: any) => {
    if (
      patientConfig?.address?.requiredHousingStatus &&
      isAllowedAddressHousingStatus &&
      isEmpty(values?.address_housing_status)
    ) {
      setExternalError(true);
      return;
    }

    setValue('address_line', data.line[0]);
    setValue('address_line_2', data.line[1]);
    setValue('address_city', data.city);
    setValue('address_district', data.district);
    setValue('address_postal', data.postalCode);
    setValue('address_state', data.state);
    setValue('address_use', data.use);
    clearErrors([
      'address_line',
      'address_city',
      'address_postal',
      'address_state',
      'address_housing_status',
    ]);
    setAddress(data);
    setOpenAddAddress(false);
    setExternalError(false);
  };

  const schema = Yup.object().shape({
    given: Yup.string()
      .required('First Name is required')
      .matches(/^[a-zA-Z\s]+$/, 'First Name cannot contain numbers'),
    middle_name: Yup.string()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .matches(/^[a-zA-Z\s]+$/, 'Middle Name cannot contain numbers'),
    family: Yup.string()
      .required('Last Name is required')
      .matches(/^[a-zA-Z\s-]+$/, 'Last Name cannot contain numbers'),
    birthdate: Yup.date()
      .nullable()
      .transform((curr, orig) => (orig === '' ? null : curr))
      .required('Date of birth is required')
      .test('birthdate', 'Date of birth cannot be a date in the future', (value) => {
        const birthDate = moment(value ?? null);
        if (birthDate?.isAfter(moment(), 'day')) {
          setError('birthdate', {
            message: 'Date of birth cannot be a date in the future',
            type: 'manual',
          });
          return false;
        }
        return true;
      }),
    gender: Yup.string().required(`
      ${i18n('patientEngagement.details.gender', 'engagement')} 
      is required`),
    emailType: Yup.string().when('emailAddress', (email, schema) => {
      if (email) return schema.required('Email Type is required');
    }),
    emailAddress: Yup.string().email('Email must be a valid email address'),
    phoneNumber: Yup.string()
      .nullable()
      .notRequired()
      .transform((value) => {
        if (!value) return null;
        const trimmed = value.trim();
        if (trimmed.length === 0) return null;
        const digitsOnly = trimmed.replace(/\D/g, '');
        return digitsOnly.length === 0 ? null : trimmed;
      })
      .test('test-phone', 'Please enter a valid phone number', function (value) {
        if (!value) return true;
        return /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(value);
      }),
    phoneType: Yup.string()
      .nullable()
      .notRequired()
      .when('phoneNumber', {
        is: (phone) => {
          if (!phone) return false;
          const digitsOnly = phone.replace(/\D/g, '');
          return digitsOnly.length > 0 && /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(phone);
        },
        then: Yup.string().required('Phone Use is required'),
      }),
    address_line: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmMember?.requiredAddress,
        then: (schema) => schema.required('Street is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_line_2: Yup.string(),
    address_city: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmMember?.requiredAddress,
        then: (schema) => schema.required('City is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_state: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmMember?.requiredAddress,
        then: (schema) => schema.required('State is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_postal: Yup.string()
      .nullable()
      .when([], {
        is: () => workflow?.confirmMember?.requiredAddress,
        then: (schema) => schema.required('Zip Code is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    address_housing_status: Yup.string()
      .nullable()
      .when([], {
        is: () => patientConfig?.address?.requiredHousingStatus && isAllowedAddressHousingStatus,
        then: (schema) => schema.required('Housing Status is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const defaultValues = useMemo(
    () => ({
      given: patient?.name?.[0].given?.[0] || '',
      middle_name: patient?.name?.[0]?.given?.[1] || '',
      family: patient?.name?.[0].family,
      birthdate: patient?.birthDate
        ? moment.utc(new Date(patient?.birthDate)).format('MM/DD/YYYY')
        : '',
      gender: patient?.gender,
      emailType: patient?.getPrimaryEmail()?.use ?? '',
      emailAddress: patient?.getPrimaryEmail()?.value ?? '',
      phoneType: patient?.getPrimaryPhone()?.use ?? '',
      phoneNumber: patient?.getPrimaryPhone()?.value ?? '',
      language: patient?.getLanguage()?.[0]?.value ?? '',
      address_use: patient?.getPrimaryAddress()?.use ?? '',
      address_line: patient?.getPrimaryAddress()?.line?.[0] ?? '',
      address_line_2: patient?.getPrimaryAddress()?.line?.[1] ?? '',
      address_city: patient?.getPrimaryAddress()?.city ?? '',
      address_state: patient?.getPrimaryAddress()?.state ?? '',
      address_postal: patient?.getPrimaryAddress()?.postalCode ?? '',
      address_district: patient?.getPrimaryAddress()?.district || '',
      address_housing_status:
        getExtensionArrayByURLs(patient?.address?.[0], [
          'http://fhir.org/guides/hrsa/uds-plus/CodeSystem/udsplus-housing-status-codes',
        ])?.[0]?.valueCode ??
        patientConfig?.address?.defaultHousingStatus ??
        '',
      organization: patient?.managingOrganization?.reference
        ? getFhirIdFromEntity(patient?.managingOrganization?.reference)
        : '',
      organizationName: patient?.managingOrganization?.display ?? '',
    }),
    [patient]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    control,
    watch,
    reset,
    setError,
    setValue,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = methods;

  const values = watch();

  const onSubmit = async (data: any) => {
    setOpenBackdrop(true);

    try {
      const coding = convertValueToValueSet(data.language || '', simpleLanguage);
      const codeValueLanguage: any = {
        language: { coding: [coding], text: coding?.display },
        preferred: true,
      };

      let getOrganization;
      if (!isOrganizationLoading && organizations?.length > 0 && data?.organization)
        getOrganization = organizations?.find((item) => item?.id === data?.organization);

      const emailIndex = patient?.telecom?.findIndex((item) => item.system === 'email');
      const phoneIndex = patient?.telecom?.findIndex((item) => item.system === 'phone');

      let patientUpdate: any = cloneDeep(patient ?? {});

      if (!patientUpdate.telecom) {
        patientUpdate.telecom = [];
      }

      if (!patientUpdate.communication) {
        patientUpdate.communication = [];
      }

      let getAddress: any;

      if (!isEmpty(address)) {
        if (isEmpty(address?.extension)) {
          address.extension = [];
        }

        if (data.address_housing_status) {
          const housingStatusExtensionIndex = patient?.address?.[0]?.extension?.findIndex(
            (item) => item?.url === housingStatus?.getConceptSystem()
          );
          const housingStatusExtension = {
            url: housingStatus?.getConceptSystem(),
            valueCode: data.address_housing_status,
          };
          if (housingStatusExtensionIndex !== undefined && housingStatusExtensionIndex !== -1) {
            address.extension[housingStatusExtensionIndex] = housingStatusExtension;
          } else {
            address.extension.push(housingStatusExtension);
          }
        }
        getAddress = address;
      }

      const emailUpdate = {
        system: 'email' as any,
        use: data.emailType as ContactPointUse,
        value: data.emailAddress,
      };

      const phoneUpdate = {
        system: 'phone' as any,
        use: data.phoneType as ContactPointUse,
        ...(data.areaCode
          ? { value: `${data.areaCode} ${data.phoneNumber}` }
          : data.phoneNumber
          ? { value: `${data.phoneNumber}` }
          : { value: null }),
      };

      if (emailIndex !== undefined && emailIndex !== -1) {
        patientUpdate.telecom[emailIndex] = emailUpdate;
      } else if (emailUpdate?.value) {
        patientUpdate.telecom.push(emailUpdate);
      }

      if (phoneIndex !== undefined && phoneIndex !== -1) {
        patientUpdate.telecom[phoneIndex] = phoneUpdate;
      } else if (phoneUpdate?.value) {
        patientUpdate.telecom.push(phoneUpdate);
      }

      if (patientUpdate.communication.length > 0 && coding?.code) {
        patientUpdate.communication[0] = codeValueLanguage;
      } else if (coding?.code) {
        patientUpdate.communication.push(codeValueLanguage);
      }

      const patientUpdated = await handlePatient(
        produce(patientUpdate!, (draft) => {
          if (!draft.name) draft.name = [];
          if (!draft.name[0]) draft.name[0] = {};
          draft.name[0].family = data.family;
          if (!draft.name[0].given) draft.name[0].given = [];
          draft.name[0].given = [
            data.given,
            ...(!isEmpty(data.middle_name) ? [data.middle_name] : []),
          ];
          draft.birthDate = moment.utc(new Date(data.birthdate)).format('YYYY-MM-DD');
          draft.gender = data.gender as PatientGender;
          if (!draft.address) draft.address = [];
          if (!isEmpty(address)) draft.address[0] = address;
          draft.managingOrganization ??= {};
          if (getOrganization?.id) {
            draft.managingOrganization.reference = `Organization/${getOrganization?.id}`;
            draft.managingOrganization.display = getOrganization?.name;
          }
        })
      );

      await handleConfirmMember(patientUpdated);
      await refreshPatient();
    } catch (error) {
      console.log('error', error);
    }
    handlerIsOpen(false);
    setOpenBackdrop(false);
  };

  const addressExternal = useMemo(
    () => ({
      id: patient?.getPrimaryAddress()?.id || '',
      line: patient?.getPrimaryAddress()?.line || '',
      city: patient?.getPrimaryAddress()?.city || '',
      district: patient?.getPrimaryAddress()?.city || '',
      state: patient?.getPrimaryAddress()?.state || '',
      postalCode: patient?.getPrimaryAddress()?.postalCode || '',
      country: patient?.getPrimaryAddress()?.country || '',
      use: patient?.getPrimaryAddress()?.use || '',
      extension: patient?.getPrimaryAddress()?.extension || '',
    }),
    [patient]
  );

  const handlerClose = () => {
    setOpenAddAddress(false);
    setExternalError(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => {
          handlerIsOpen(false);
        }}
      >
        {patientInfoLoaded && patient !== null ? (
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Backdrop
              sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={openBackdrop}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <List component="nav">
              <ListItem>
                <Grid container>
                  <Stack direction="row" spacing={2}>
                    <Button variant="contained" type="submit" disabled={isOrganizationLoading}>
                      Confirm Information
                    </Button>
                    <Button
                      onClick={() => {
                        handlerIsOpen(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </ListItem>
              <Divider />
              <ListItem>
                <Grid container direction="column" marginTop={2} width={800}>
                  <Grid container direction="row" alignItems="center">
                    <Grid container item xs={3}>
                      <Typography>First Name*</Typography>
                    </Grid>
                    <Grid container item xs={8}>
                      {firstNameEditMode ? (
                        <RHFTextField
                          name="given"
                          label="First Name*"
                          disabled={!firstNameEditMode}
                        />
                      ) : (
                        <Stack>
                          <Typography>
                            <b>{values.given}</b>
                          </Typography>

                          {!!errors.given && (
                            <FormHelperText error> {errors.given?.message}</FormHelperText>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setFirstNameEditMode((prev) => !prev)}>
                        {firstNameEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Middle Name</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {middleNameEditMode ? (
                        <RHFTextField name="middle_name" label="Middle Name" />
                      ) : (
                        <Stack>
                          <Typography>
                            <b>{values.middle_name}</b>
                          </Typography>

                          {!!errors.middle_name && (
                            <FormHelperText error> {errors.middle_name?.message}</FormHelperText>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setMiddleNameEditMode((prev) => !prev)}>
                        {middleNameEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Last Name*</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {secondNameEditMode ? (
                        <RHFTextField name="family" label="Last Name*" />
                      ) : (
                        <Stack>
                          <Typography>
                            <b>{values.family}</b>
                          </Typography>

                          {!!errors.family && (
                            <FormHelperText error> {errors.family?.message}</FormHelperText>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setSecondNameEditMode((prev) => !prev)}>
                        {secondNameEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Date of Birth*</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {dobEditMode ? (
                        <Controller
                          name="birthdate"
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <MobileDatePicker
                              label="Date of Birth*"
                              value={field.value || null}
                              onChange={(newValue) => {
                                field.onChange(newValue);
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  error={!!error}
                                  helperText={error?.message}
                                />
                              )}
                            />
                          )}
                        />
                      ) : (
                        <Stack>
                          <Typography>
                            <b>{values.birthdate && moment.utc(values.birthdate).format('l')}</b>
                          </Typography>

                          {!!errors.birthdate && (
                            <FormHelperText error> {errors.birthdate?.message}</FormHelperText>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setDobEditMode((prev) => !prev)}>
                        {dobEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>
                        {i18n('patientEngagement.details.gender', 'engagement')}*
                      </Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {genderEditMode ? (
                        <RHFSelect
                          name="gender"
                          label={`${i18n('patientEngagement.details.gender', 'engagement')}*`}
                          fullWidth={true}
                        >
                          <MenuItem disabled />
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                          <MenuItem value="unknown">Unknown</MenuItem>
                        </RHFSelect>
                      ) : (
                        <Stack>
                          <Typography>
                            <b>{capitalCase(values.gender || '')}</b>
                          </Typography>

                          {!!errors.gender && (
                            <FormHelperText error> {errors.gender?.message}</FormHelperText>
                          )}
                        </Stack>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setGenderEditMode((prev) => !prev)}>
                        {genderEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Email</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {emailEditMode ? (
                        <>
                          <Grid xs={3}>
                            <RHFSelect name="emailType" label="Email Type" fullWidth={true}>
                              <MenuItem disabled />
                              <MenuItem value="home">Home</MenuItem>
                              <MenuItem value="work">Work</MenuItem>
                            </RHFSelect>
                          </Grid>

                          <Grid xs={9}>
                            <RHFTextField name="emailAddress" label="Email" fullWidth />
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid xs={3}>
                            <Typography>
                              <b>{capitalCase(values.emailType)}</b>
                            </Typography>

                            {!!errors.emailType && (
                              <FormHelperText error> {errors.emailType?.message}</FormHelperText>
                            )}
                          </Grid>

                          <Grid xs={9}>
                            <Typography>
                              <b>{values.emailAddress}</b>
                            </Typography>

                            {!!errors.emailAddress && (
                              <FormHelperText error> {errors.emailAddress?.message}</FormHelperText>
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setEmailEditMode((prev) => !prev)}>
                        {emailEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Phone</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {phoneEditMode ? (
                        <>
                          <Grid xs={3}>
                            <RHFSelect name="phoneType" label="Phone Type" fullWidth={true}>
                              <MenuItem disabled />
                              <MenuItem value="home">Home</MenuItem>
                              <MenuItem value="work">Work</MenuItem>
                              <MenuItem value="mobile">Mobile</MenuItem>
                            </RHFSelect>
                          </Grid>
                          <Grid xs={9}>
                            <Controller
                              name="phoneNumber"
                              control={control}
                              render={({
                                field: { onChange, value, onBlur },
                                fieldState: { error, isDirty },
                              }) => {
                                const isPlaceholder = (val: string | null | undefined) => {
                                  if (!val) return true;
                                  const digitsOnly = val.replace(/\D/g, '');
                                  return digitsOnly.length === 0;
                                };

                                const digitsOnly = value ? value.replace(/\D/g, '') : '';
                                const hasValue = digitsOnly.length > 0;
                                const shouldShowError = isDirty && hasValue && !!error?.message;

                                const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                  const newValue = e.target.value;
                                  if (isPlaceholder(newValue)) {
                                    onChange('');
                                  } else {
                                    onChange(newValue);
                                  }
                                };

                                return (
                                  <InputMask
                                    mask="(999) 999-9999"
                                    value={value || ''}
                                    onChange={handleChange}
                                    onBlur={onBlur}
                                  >
                                    {(inputProps: any) => (
                                      <TextField
                                        label="Phone Number"
                                        {...inputProps}
                                        error={shouldShowError}
                                        helperText={shouldShowError ? error?.message : ''}
                                      />
                                    )}
                                  </InputMask>
                                );
                              }}
                            />
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid xs={3}>
                            <Typography>
                              <b>{capitalCase(values.phoneType)}</b>
                            </Typography>

                            {!!errors.phoneType && (
                              <FormHelperText error> {errors.phoneType?.message}</FormHelperText>
                            )}
                          </Grid>

                          <Grid xs={9}>
                            <Typography>
                              <b>{values.phoneNumber ? phoneFormat(values.phoneNumber) : ''}</b>
                            </Typography>

                            {!!errors.phoneNumber && (
                              <FormHelperText error> {errors.phoneNumber?.message}</FormHelperText>
                            )}
                          </Grid>
                        </>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setPhoneEditMode((prev) => !prev)}>
                        {phoneEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Language</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {languageEditMode ? (
                        <RHFSelect name="language" label="Language" fullWidth={true}>
                          <MenuItem disabled />
                          {simpleLanguage?.asList().map((option) => (
                            <MenuItem key={option.code} value={option.display}>
                              {option.display}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                      ) : (
                        <Stack>
                          <Typography>
                            <b>{values.language}</b>
                          </Typography>
                        </Stack>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setLanguageEditMode((prev) => !prev)}>
                        {languageEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center">
                    <Grid container item xs={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography>
                          Address {workflow?.confirmMember?.requiredAddress && '*'}
                        </Typography>
                        <Tooltip
                          title={i18n(
                            'patientEngagement.details.workflow.checklist.confirmMember.referLocalPolicy',
                            'engagement'
                          )}
                          placement="top"
                        >
                          <Info htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                        </Tooltip>
                      </Box>
                    </Grid>

                    <Grid container item xs={8}>
                      {addressEditMode ? (
                        <>
                          <Grid
                            xs={12}
                            marginTop={1}
                            marginBottom={1}
                            display="flex"
                            justifyContent="flex-end"
                          >
                            <Box
                              sx={{
                                paddingY: 2,
                                paddingX: 2,
                                backgroundColor: '#cafdf5',
                                borderRadius: 1,
                                gap: 12,
                              }}
                            >
                              <Box
                                sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                              >
                                <Info htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#003768', fontSize: '0.94rem' }}
                                >
                                  {i18n(
                                    'patientEngagement.details.workflow.checklist.confirmMember.referLocalPolicy',
                                    'engagement'
                                  )}
                                </Typography>
                                <Button
                                  color="primary"
                                  startIcon={<Add />}
                                  variant="contained"
                                  sx={{ marginLeft: 1.5 }}
                                  onClick={() => setOpenAddAddress(true)}
                                >
                                  Add
                                </Button>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid xs={6} marginTop={1}>
                            <RHFTextField name="address_line" label="Street*" disabled />
                          </Grid>
                          <Grid xs={6} marginTop={1}>
                            <RHFTextField name="address_line_2" label="Street 2" disabled />
                          </Grid>
                          <Grid xs={6} marginTop={1}>
                            <RHFTextField name="address_city" label="City*" disabled />
                          </Grid>
                          <Grid xs={6} marginTop={1}>
                            <RHFTextField name="address_state" label="State*" disabled />
                          </Grid>
                          <Grid xs={6} marginTop={1}>
                            <RHFTextField name="address_district" label="County" disabled />
                          </Grid>
                          <Grid xs={6} marginTop={1}>
                            <RHFTextField name="address_postal" label="Zip Code*" disabled />
                          </Grid>
                          {isAllowedAddressHousingStatus && (
                            <Grid xs={6} marginTop={1}>
                              <RHFTextField
                                name="address_housing_status"
                                label={`Housing Status ${
                                  patientConfig?.address?.requiredHousingStatus ? '*' : ''
                                }`}
                                value={
                                  values.address_housing_status
                                    ? capitalize(
                                        values?.address_housing_status?.replaceAll('-', ' ')
                                      )
                                    : ''
                                }
                                disabled
                              />
                            </Grid>
                          )}
                        </>
                      ) : (
                        <Grid xs={9}>
                          <Typography>
                            <b>{`${values.address_line || ''}, ${values.address_line_2 || ''} ${
                              values.address_city || ''
                            }, 
                                  ${values.address_state || ''}, ${
                              values.address_district || ''
                            },  ${values.address_postal || ''},
                                  ${
                                    values.address_housing_status && isAllowedAddressHousingStatus
                                      ? capitalize(
                                          values?.address_housing_status?.replaceAll('-', ' ')
                                        )
                                      : ''
                                  }`}</b>
                          </Typography>
                          <Stack>
                            {!!errors.address_line && (
                              <FormHelperText error> {errors.address_line?.message}</FormHelperText>
                            )}

                            {!!errors.address_city && (
                              <FormHelperText error> {errors.address_city?.message}</FormHelperText>
                            )}

                            {!!errors.address_state && (
                              <FormHelperText error>
                                {' '}
                                {errors.address_state?.message}
                              </FormHelperText>
                            )}

                            {!!errors.address_postal && (
                              <FormHelperText error>
                                {' '}
                                {errors.address_postal?.message}
                              </FormHelperText>
                            )}

                            {!!errors.address_housing_status && !openAddAddress && (
                              <FormHelperText error>
                                {' '}
                                {errors.address_housing_status?.message}
                              </FormHelperText>
                            )}
                          </Stack>
                        </Grid>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setAddressEditMode((prev) => !prev)}>
                        {addressEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" alignItems="center" sx={{ mt: 2 }}>
                    <Grid container item xs={3}>
                      <Typography>Organization</Typography>
                    </Grid>

                    <Grid container item xs={8}>
                      {organizationEditMode ? (
                        <RHFSelect name="organization" label="Organization" fullWidth={true}>
                          {organizations?.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                      ) : (
                        <Typography>
                          <b>{values.organizationName}</b>
                        </Typography>
                      )}
                    </Grid>

                    <Grid container item xs={1}>
                      <IconButton onClick={() => setOrganizationEditMode((prev) => !prev)}>
                        {organizationEditMode ? <Check /> : <Edit />}
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </ListItem>
            </List>
          </FormProvider>
        ) : (
          <Stack width={500} alignItems="center" marginTop={50}>
            <CircularProgress />
          </Stack>
        )}
      </Drawer>
      <Dialog open={openAddAddress} fullWidth maxWidth="md">
        <DialogTitle> Add Address</DialogTitle>
        <Card sx={{ p: 2, overflowY: 'scroll' }}>
          <AddAddressLoading
            isEditable={true}
            externalAddress={addressExternal}
            handleClose={handlerClose}
            handleSave={saveChanges}
            externalError={externalError}
            {...(isAllowedAddressHousingStatus
              ? {
                  childrenTop: (
                    <ListItem>
                      <FormControl fullWidth error={externalError}>
                        <InputLabel id="address-housing-status-label">
                          Housing Status {patientConfig?.address?.requiredHousingStatus ? '*' : ''}
                        </InputLabel>
                        <Select
                          labelId="address-housing-status-label"
                          id="addressHousingStatus"
                          name="addressHousingStatus"
                          label={`Housing Status ${
                            patientConfig?.address?.requiredHousingStatus ? '*' : ''
                          }`}
                          value={values.address_housing_status ?? ''}
                          onChange={(ev) => {
                            setValue('address_housing_status', String(ev.target.value));
                            setExternalError(false);
                          }}
                        >
                          {housingStatus?.asList().map((option) => (
                            <MenuItem key={option.code} value={option.code}>
                              {capitalize(option?.code?.replaceAll('-', ' '))}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>
                  ),
                }
              : {})}
          />
        </Card>
      </Dialog>
    </>
  );
}
